// src/components/PartTemplatesManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection, getDocs, doc, getDoc, setDoc, deleteDoc,
  serverTimestamp, query, orderBy
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

/* ======================= Constants & Utils ======================= */

const COLLECTION = "part_templates";

/** System codes: always present in saved data, hidden in the UI by default */
const SYSTEM_DEFAULTS = [
  { code: "UNKNOWN", description: "Unknown reason" },
  { code: "OTHER",   description: "Other / specify" },
  { code: "NOFAULT", description: "No fault found" },
];

const EMPTY_TEMPLATE = {
  partCode: "",
  title: "",
  description: "",
  active: true,
  codes: [],   // will be normalized for UI (with _id & _system)
  steps: [],
};

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const safeId = (s = "") => s.trim();

/** Normalize codes for editing (stable _id + inject system defaults) */
function normalizeCodesForEdit(codes = []) {
  const up = (s) => String(s || "").toUpperCase();
  const existing = [...codes];

  // ensure system codes exist; mark them _system (hidden by default in editor)
  SYSTEM_DEFAULTS.forEach((sys) => {
    const hit = existing.find((c) => up(c.code) === sys.code);
    if (hit) {
      hit._system = true;
      if (!hit.description) hit.description = sys.description;
    } else {
      existing.push({ ...sys, deprecated: false, _system: true });
    }
  });

  // assign stable _id per row (don’t use code as key)
  return existing.map((c) => ({ _id: c._id || uid(), ...c }));
}

/** Strip UI-only fields and ensure system codes exist when saving */
function prepareCodesForSave(codes = []) {
  const up = (s) => String(s || "").toUpperCase();

  // remove empty rows
  const cleaned = (codes || []).filter((c) => up(c.code).trim() !== "");

  // de-dupe by code (keep first)
  const seen = new Set();
  const deduped = [];
  for (const c of cleaned) {
    const k = up(c.code);
    if (seen.has(k)) continue;
    seen.add(k);
    deduped.push({
      code: k,
      description: c.description || "",
      deprecated: !!c.deprecated,
    });
  }

  // ensure system defaults present
  SYSTEM_DEFAULTS.forEach((sys) => {
    if (!deduped.some((c) => up(c.code) === sys.code)) {
      deduped.push({ code: sys.code, description: sys.description, deprecated: false });
    }
  });

  return deduped;
}

const deriveStepId = (label = "") =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 40) ||
  `step-${Date.now()}`;

/* ======================= Component ======================= */

export default function PartTemplatesManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qtext, setQtext] = useState("");

  const [edit, setEdit] = useState(null);    // current editor state (object)
  const [originalId, setOriginalId] = useState(""); // doc id we opened (for rename/delete)

  const [copyFrom, setCopyFrom] = useState(""); // partCode to copy from
  const [jsonPanelOpen, setJsonPanelOpen] = useState(false);
  const [jsonText, setJsonText] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, COLLECTION), orderBy("partCode")));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const t = qtext.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) =>
      (r.partCode || "").toLowerCase().includes(t) ||
      (r.title || "").toLowerCase().includes(t) ||
      (r.description || "").toLowerCase().includes(t)
    );
  }, [rows, qtext]);

  /* -------------------- CRUD -------------------- */

  const startNew = () => {
    setEdit({ ...EMPTY_TEMPLATE, codes: normalizeCodesForEdit([]) });
    setOriginalId("");
    setCopyFrom("");
    setJsonPanelOpen(false);
    setJsonText("");
  };

  const startEdit = async (row) => {
    const ref = doc(db, COLLECTION, row.partCode || row.id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    setEdit({
      ...EMPTY_TEMPLATE,
      ...data,
      codes: normalizeCodesForEdit(data.codes || []),
    });
    setOriginalId(row.partCode || row.id);
    setCopyFrom("");
    setJsonPanelOpen(false);
    setJsonText("");
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete template ${row.partCode || row.id}?`)) return;
    await deleteDoc(doc(db, COLLECTION, row.partCode || row.id));
    await refresh();
    if (originalId === (row.partCode || row.id)) {
      setEdit(null);
      setOriginalId("");
    }
  };

  const doCopyFrom = async () => {
    const id = safeId(copyFrom);
    if (!id) return;
    const ref = doc(db, COLLECTION, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) { alert("Template not found."); return; }
    const data = snap.data();
    setEdit((prev) => ({
      ...(prev || EMPTY_TEMPLATE),
      title: data.title || "",
      description: data.description || "",
      active: data.active !== false,
      codes: normalizeCodesForEdit(data.codes || []),
      steps: (data.steps || []).map((s, idx) => ({
        id: s.id || deriveStepId(s.label || `step-${idx + 1}`),
        label: s.label || `Step ${idx + 1}`,
        type: s.type === "number" ? "number" : s.type === "check" ? "check" : "text",
        required: !!s.required,
        placeholder: s.placeholder || "",
      })),
    }));
  };

  const save = async () => {
    if (!edit) return;
    const partCode = safeId(edit.partCode);
    if (!partCode) { alert("partCode is required (this will be the document id)."); return; }

    const payload = {
      partCode,
      title: edit.title || "",
      description: edit.description || "",
      active: !!edit.active,
      codes: prepareCodesForSave(edit.codes || []),
      steps: (edit.steps || []).map((s, idx) => ({
        id: s.id || deriveStepId(s.label || `step-${idx + 1}`),
        label: s.label || `Step ${idx + 1}`,
        type: s.type === "number" ? "number" : s.type === "check" ? "check" : "text",
        required: !!s.required,
        placeholder: s.placeholder || "",
      })),
      updatedAt: serverTimestamp(),
    };

    if (originalId && originalId !== partCode) {
      if (!window.confirm(`You changed the partCode from ${originalId} to ${partCode}. Create a new doc and remove the old one?`)) {
        return;
      }
      await setDoc(doc(db, COLLECTION, partCode), payload);
      await deleteDoc(doc(db, COLLECTION, originalId));
      setOriginalId(partCode);
    } else {
      await setDoc(doc(db, COLLECTION, partCode), payload, { merge: true });
      setOriginalId(partCode);
    }

    await refresh();
    alert("Saved.");
  };

  /* -------------------- JSON import/export -------------------- */

  const exportJSON = () => {
    if (!edit) return;
    const out = JSON.stringify(edit, null, 2);
    setJsonText(out);
    setJsonPanelOpen(true);
  };

  const importJSON = () => {
    try {
      const parsed = JSON.parse(jsonText);
      parsed.codes = normalizeCodesForEdit(parsed.codes || []);
      setEdit((prev) => ({ ...(prev || EMPTY_TEMPLATE), ...parsed }));
      alert("Imported into editor (not saved yet).");
    } catch {
      alert("Invalid JSON.");
    }
  };

  /* ======================= Render ======================= */

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-2xl font-bold">Part Templates</h1>
        <input
          className="ml-auto border rounded-md px-3 py-2 text-sm"
          placeholder="Search part code / title / description…"
          value={qtext}
          onChange={(e) => setQtext(e.target.value)}
        />
        <button className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50" onClick={startNew}>
          New
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        {/* List */}
        <aside className="bg-white border rounded-lg">
          <div className="p-2 border-b text-xs text-gray-500">
            {loading ? "Loading…" : `${filtered.length} templates`}
          </div>
          <ul className="max-h-[70vh] overflow-auto divide-y">
            {filtered.map((r) => (
              <li key={r.partCode || r.id} className="p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{r.partCode}</div>
                  <div className="text-sm text-gray-600 truncate">{r.title || "—"}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${r.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                  {r.active ? "Active" : "Inactive"}
                </span>
                <button className="text-indigo-600 text-sm hover:underline" onClick={() => startEdit(r)}>
                  Edit
                </button>
                <button className="text-red-600 text-sm hover:underline" onClick={() => remove(r)}>
                  Delete
                </button>
              </li>
            ))}
            {!loading && filtered.length === 0 && (
              <li className="p-3 text-gray-500 text-sm">No templates.</li>
            )}
          </ul>
        </aside>

        {/* Editor */}
        <section className="bg-white border rounded-lg p-4">
          {!edit ? (
            <div className="text-gray-500">
              Pick a template or click <b>New</b>.
            </div>
          ) : (
            <div className="space-y-6">
              {/* Top bar */}
              <div className="flex items-center gap-2">
                <div className="text-sm text-gray-600">
                  {originalId ? <>Editing <span className="font-mono">{originalId}</span></> : "New template"}
                </div>
                <button className="ml-auto px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50" onClick={save}>
                  Save
                </button>
                <button className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50" onClick={exportJSON}>
                  Export JSON
                </button>
                <button
                  className={`px-3 py-2 rounded-md border ${jsonPanelOpen ? "border-indigo-400 bg-indigo-50" : "border-gray-300 hover:bg-gray-50"}`}
                  onClick={() => setJsonPanelOpen((v) => !v)}
                >
                  {jsonPanelOpen ? "Hide JSON" : "Import JSON"}
                </button>
              </div>

              {/* Basic fields */}
              <div className="grid sm:grid-cols-2 gap-4">
                <Field
                  label="Part Code (document id)"
                  value={edit.partCode}
                  onChange={(v) => setEdit((p) => ({ ...p, partCode: v }))}
                  placeholder="e.g., MV-15"
                />
                <div className="flex items-end gap-2">
                  <Field
                    label="Active"
                    render={() => (
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!edit.active}
                          onChange={(e) => setEdit((p) => ({ ...p, active: e.target.checked }))}
                        />
                        <span>{edit.active ? "Active" : "Inactive"}</span>
                      </label>
                    )}
                  />
                </div>
                <Field
                  label="Title"
                  value={edit.title}
                  onChange={(v) => setEdit((p) => ({ ...p, title: v }))}
                />
                <Field
                  label="Description"
                  value={edit.description}
                  onChange={(v) => setEdit((p) => ({ ...p, description: v }))}
                />
              </div>

              {/* Copy from existing */}
              <div className="border rounded-lg p-3">
                <div className="text-sm font-medium mb-2">Copy from another part</div>
                <div className="flex gap-2">
                  <input
                    className="border rounded-md px-3 py-2 text-sm flex-1"
                    placeholder="Enter partCode to copy from"
                    value={copyFrom}
                    onChange={(e) => setCopyFrom(e.target.value)}
                  />
                  <button className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50" onClick={doCopyFrom}>
                    Copy
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Copies title, description, codes, and steps. System codes are enforced automatically.
                </p>
              </div>

              {/* Codes editor */}
              <CodesEditor
                codes={edit.codes}
                onChange={(next) => setEdit((p) => ({ ...p, codes: next }))}
              />

              {/* Steps editor */}
              <StepsEditor
                steps={edit.steps}
                onChange={(next) => setEdit((p) => ({ ...p, steps: next }))}
              />

              {/* JSON panel */}
              {jsonPanelOpen && (
                <div className="border rounded-lg p-3">
                  <div className="text-sm font-medium mb-2">Import / edit raw JSON (current template)</div>
                  <textarea
                    className="w-full border rounded-md p-2 font-mono text-xs"
                    rows={10}
                    value={jsonText}
                    onChange={(e) => setJsonText(e.target.value)}
                    placeholder='Paste JSON here and click "Import into editor"'
                  />
                  <div className="mt-2 flex gap-2">
                    <button className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50" onClick={importJSON}>
                      Import into editor
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/* ======================= Small UI helpers ======================= */

function Field({ label, value, onChange, placeholder, render }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {render ? (
        render()
      ) : (
        <input
          className="w-full border rounded-md px-3 py-2 text-sm"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

/* -------- Codes Editor (stable keys, no live sorting, hides system by default) -------- */

function CodesEditor({ codes = [], onChange }) {
  const [showSystem, setShowSystem] = useState(false);

  const setById = (id, patch) => {
    onChange((codes || []).map(c => (c._id === id ? { ...c, ...patch } : c)));
  };

  const addCode = () => {
    onChange([...(codes || []), { _id: uid(), code: "", description: "", deprecated: false }]);
  };

  const delById = (id) => {
    const c = (codes || []).find(x => x._id === id);
    if (c?._system) return; // can't delete system codes
    onChange((codes || []).filter(x => x._id !== id));
  };

  const visible = (codes || []).filter(c => showSystem || !c._system);

  return (
    <div className="border rounded-lg p-3">
      <div className="flex items-center gap-2">
        <div className="text-sm font-medium">Codes (used for both Reported Reason & Fault Found)</div>
        <button
          className="ml-auto px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
          onClick={addCode}
          type="button"
        >
          + Add code
        </button>
        <label className="inline-flex items-center gap-2 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={showSystem}
            onChange={(e) => setShowSystem(e.target.checked)}
          />
          <span>Show system codes</span>
        </label>
      </div>

      <div className="mt-3 grid grid-cols-[140px_1fr_120px_60px] gap-2 items-center text-xs font-medium text-gray-600">
        <div>Code</div><div>Description</div><div>Status</div><div>Del</div>
      </div>

      <div className="mt-1 space-y-2">
        {visible.map((c) => (
          <div
            key={c._id}
            className={`grid grid-cols-[140px_1fr_120px_60px] gap-2 items-center ${c._system ? "opacity-60" : ""}`}
          >
            <input
              className="border rounded px-2 py-1 text-sm"
              value={c.code || ""}
              onChange={(e) => setById(c._id, { code: e.target.value.toUpperCase() })}
              placeholder="e.g., LEAK"
            />
            <input
              className="border rounded px-2 py-1 text-sm"
              value={c.description || ""}
              onChange={(e) => setById(c._id, { description: e.target.value })}
              placeholder="Short description"
            />
            <label className="inline-flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={!!c.deprecated}
                onChange={(e) => setById(c._id, { deprecated: e.target.checked })}
              />
              <span>Deprecated</span>
            </label>
            <button
              className={`px-2 py-1 rounded border text-sm ${c._system
                ? "border-gray-200 text-gray-400 cursor-not-allowed"
                : "border-red-200 text-red-700 hover:bg-red-50"}`}
              onClick={() => delById(c._id)}
              type="button"
              disabled={!!c._system}
              title={c._system ? "System code cannot be removed" : "Delete"}
            >
              X
            </button>
          </div>
        ))}
        {visible.length === 0 && <div className="text-sm text-gray-500">No custom codes yet.</div>}
      </div>

      {!showSystem && (
        <p className="text-xs text-gray-500 mt-2">
          System codes (<code>UNKNOWN</code>, <code>OTHER</code>, <code>NOFAULT</code>) are hidden but will always be saved.
        </p>
      )}
    </div>
  );
}

/* -------- Steps Editor -------- */

function StepsEditor({ steps = [], onChange }) {
  const setAt = (i, patch) => {
    const next = [...steps];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };

  const addStep = () => {
    onChange([...(steps || []), { id: "", label: "", type: "check", required: false, placeholder: "" }]);
  };

  const delAt = (i) => {
    onChange(steps.filter((_, idx) => idx !== i));
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="border rounded-lg p-3">
      <div className="flex items-center">
        <div className="text-sm font-medium">Test Procedure Steps</div>
        <button className="ml-auto px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 text-sm" onClick={addStep} type="button">
          + Add step
        </button>
      </div>

      <div className="mt-3 space-y-3">
        {steps.map((s, i) => (
          <div key={i} className="border rounded-md p-3">
            <div className="grid sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Step ID</label>
                <input
                  className="border rounded px-2 py-1 text-sm w-full"
                  value={s.id || ""}
                  onChange={(e) => setAt(i, { id: e.target.value })}
                  placeholder="Unique (auto-generate if left blank)"
                  onBlur={() => { if (!s.id) setAt(i, { id: deriveStepId(s.label) }); }}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Label</label>
                <input
                  className="border rounded px-2 py-1 text-sm w-full"
                  value={s.label || ""}
                  onChange={(e) => setAt(i, { label: e.target.value })}
                  placeholder="e.g., Bench pressure test (bar)"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Type</label>
                <select
                  className="border rounded px-2 py-1 text-sm w-full"
                  value={s.type || "check"}
                  onChange={(e) => setAt(i, { type: e.target.value })}
                >
                  <option value="check">Check (true/false)</option>
                  <option value="number">Number</option>
                  <option value="text">Text</option>
                </select>
              </div>
              <div className="flex items-end justify-between gap-2">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!s.required}
                    onChange={(e) => setAt(i, { required: e.target.checked })}
                  />
                  <span>Required</span>
                </label>
                <div className="flex items-center gap-2">
                  <button type="button" className="px-2 py-1 rounded border border-gray-300 text-sm" onClick={() => move(i, -1)}>↑</button>
                  <button type="button" className="px-2 py-1 rounded border border-gray-300 text-sm" onClick={() => move(i, 1)}>↓</button>
                  <button type="button" className="px-2 py-1 rounded border border-red-200 text-red-700 text-sm hover:bg-red-50" onClick={() => delAt(i)}>Delete</button>
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Placeholder (optional)</label>
                <input
                  className="border rounded px-2 py-1 text-sm w-full"
                  value={s.placeholder || ""}
                  onChange={(e) => setAt(i, { placeholder: e.target.value })}
                  placeholder="Shown as hint for text/number inputs"
                />
              </div>
            </div>
          </div>
        ))}
        {steps.length === 0 && <div className="text-sm text-gray-500">No steps yet.</div>}
      </div>
    </div>
  );
}
